import { useEffect, useRef } from 'react';

import type { ImageSequencePlayerProps } from './types';

export function useLogic(props: ImageSequencePlayerProps) {
  const {
    defaultFrameIndex,
    startFrameIndex,
    onFrameChange,
    reverse,
    enabled,
    paused,
    delayBetweenIterations,
    fps = 0,
    loopCount,
    frames,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const requestId = useRef<number | null>(null);
  const frameIndex = useRef(defaultFrameIndex || 0);
  const isDelay = useRef(false);
  const direction = useRef(1);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!frames.length) {
      return;
    }
    const clear = () => {
      if (requestId.current) {
        cancelAnimationFrame(requestId.current);
        clearTimeout(timeoutRef.current!);
        isDelay.current = false;
      }
    };
    if (!canvasRef.current) {
      return clear;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return clear;
    }

    if (frameIndex.current > frames.length) {
      frameIndex.current = 0;
    }

    if (!frames.length) {
      return clear;
    }

    canvas.width = frames[0].width;
    canvas.height = frames[0].height;

    if (reverse) {
      direction.current = -1;
      frameIndex.current = frames.length - 1;
    } else {
      direction.current = 1;
      frameIndex.current = 0;
    }

    if (!enabled) {
      ctx.drawImage(frames[0], 0, 0);
      return;
    }

    let loopIndex = 0;

    // Calculate the interval for the desired FPS
    const frameInterval = 1000 / fps;
    let lastTime = 0;

    const draw = (time: number) => {
      if (!ctx) {
        return clear();
      }

      // Check if enough time has passed since the last frame
      if (time - lastTime >= frameInterval) {
        lastTime = time; // Update the last render time

        if (isDelay.current) {
          requestId.current = requestAnimationFrame(draw);
          return;
        }

        ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear the canvas
        ctx.drawImage(frames[frameIndex.current], 0, 0); // Draw the current frame
        frameIndex.current += paused ? 0 : direction.current;

        onFrameChange?.({
          frameIndex: frameIndex.current,
        });
        if (!reverse) {
          if (frameIndex.current >= frames.length) {
            frameIndex.current = 0;
            loopIndex++;

            if (delayBetweenIterations && !isDelay.current) {
              isDelay.current = true;
              timeoutRef.current = setTimeout(() => {
                isDelay.current = false;
              }, delayBetweenIterations * 1000);
            }
          }
        } else {
          if (frameIndex.current < (startFrameIndex || 0)) {
            frameIndex.current = frames.length - 1;
            loopIndex++;
            if (delayBetweenIterations && !isDelay.current) {
              isDelay.current = true;
              timeoutRef.current = setTimeout(() => {
                isDelay.current = false;
              }, delayBetweenIterations * 1000);
            }
          }
        }

        // Stop if the loop count is reached
        if (loopCount && loopIndex >= loopCount) {
          return clear;
        }
      }

      // Request the next animation frame
      requestId.current = requestAnimationFrame(draw);
    };

    requestId.current = requestAnimationFrame(draw); // Start the animation loop

    return clear;
  }, [enabled, fps, loopCount, paused, onFrameChange, delayBetweenIterations, reverse, startFrameIndex, frames]);

  return { canvasRef };
}
